import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "antd";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DynamicChart from "../components/charts/dynamicChart";
import DealsSummaryTable from "../components/sections/thd/dealSummaryTable";
import FiltersSection from "../components/sections/filters";
import MainCards from "../components/sections/mainCards";
import ThdMainCards from "../components/sections/thd/mainCards";
import Loader from "../components/layout/loader";
import {
    changeDimension,
    getDealsDetailData,
} from "../stateManagement/actions/report";
import { chartPreparation } from "../services/dataHandling";
import { dailyTotals } from "../services/dealsTableData";
import { PARTITION_MAIN, PARTITION_THD } from "../utils/constants";

const WEBSITE_DIMENSIONS = [
    { label: "Website", value: "website", key: "website" },
];
const DealDetailsPage = () => {
    const dispatch = useDispatch();
    const { dealName } = useParams();
    const location = useLocation();
    const { period, dimension, filterBy, publisherView } = useSelector(
        (state) => state.reportReducer,
    );
    const [result, setResult] = useState({ key: null, report: [] });
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);
    const [selectedCard, setSelectedCard] = useState("revenue");
    const isThd = location.pathname.startsWith("/thd/");
    const partition = isThd ? PARTITION_THD : PARTITION_MAIN;
    const SelectedMainCards = isThd ? ThdMainCards : MainCards;
    const detailQuery = useMemo(() => {
        const query = new URLSearchParams(location.search);
        const detailType =
            query.get("scope") === "advertiser" ? "advertiser" : "deal";
        return {
            dealName,
            detailType,
            ...(detailType === "advertiser" && query.get("missing") === "1"
                ? { advertiserMissing: "true" }
                : {}),
            ...(detailType === "deal" && query.has("advertiser")
                ? { orderAdvertiser: query.get("advertiser") }
                : {}),
        };
    }, [dealName, location.search]);
    const requestKey = JSON.stringify({
        period,
        filterBy,
        publisherView,
        partition,
        ...detailQuery,
        retry,
    });
    const ready = result.key === requestKey && !error;
    const fullReport = ready ? result.report : [];
    useEffect(() => {
        if (dimension !== "website") dispatch(changeDimension("website"));
    }, [dispatch, dimension]);
    useEffect(() => {
        let active = true;
        setError("");
        dispatch(
            getDealsDetailData({
                period,
                dimension: "website",
                filterBy,
                publisherView,
                partition,
                ...detailQuery,
            }),
        )
            .then((report) => {
                if (active) setResult({ key: requestKey, report });
            })
            .catch(() => {
                if (active) setError("Could not load this report.");
            });
        return () => {
            active = false;
        };
    }, [
        dispatch,
        period,
        filterBy,
        publisherView,
        partition,
        detailQuery,
        requestKey,
    ]);

    const chartData = useMemo(() => {
        // Feed one total per day to the existing chart, scoped to this deal/advertiser.
        if (!ready) return { topValues: [], data: [] };
        const chartRows = dailyTotals(result.report).map((row) => ({
            ...row,
            detailLabel: dealName,
        }));
        return chartPreparation(chartRows, "detailLabel", selectedCard, period);
    }, [ready, result.report, dealName, selectedCard, period]);
    return (
        <>
            <h2>
                {detailQuery.detailType === "advertiser"
                    ? "Advertiser"
                    : "Deal"}
                : {dealName}
            </h2>
            <FiltersSection
                dimensions={WEBSITE_DIMENSIONS}
                defaultSelection="website"
            />
            {error ? (
                <Alert
                    type="error"
                    showIcon
                    message={error}
                    action={
                        <Button onClick={() => setRetry((value) => value + 1)}>
                            Retry
                        </Button>
                    }
                />
            ) : !ready ? (
                <Loader />
            ) : (
                <>
                    <div className="main-section">
                        <SelectedMainCards
                            partition={partition}
                            reportScope="deals"
                            detailQuery={detailQuery}
                            fullData={fullReport}
                            trigger={setSelectedCard}
                            currentState={selectedCard}
                            limited
                        />
                        <DynamicChart
                            fullData={chartData}
                            currentState={selectedCard}
                        />
                    </div>
                    <DealsSummaryTable
                        fullData={fullReport}
                        dimensionOverride="website"
                        detailQuery={detailQuery}
                        allowAdvertiserGrouping={false}
                        enableNavigation={false}
                        ready={ready}
                    />
                </>
            )}
        </>
    );
};
export default DealDetailsPage;
